import React, { useCallback, useMemo, useState } from 'react';
import { RawDraftContentState } from 'draft-js';
import { isNull, isUndefined } from 'lodash/fp';

import { Position, PositionModal } from './PositionModal';
import styles from './Modal.module.scss';
import { isEmpty, WYSIWYG } from '../wysiwyg/WYSIWYG';
import { ModalHeader } from './ModalHeader';
import { LongText } from '../icons';

const MODAL_PADDING = 10;
const MODAL_HEADER_AND_WYSIWYG_BORDER = 58;

interface PopoutWysiwygModalProps {
    triggerElement: React.ReactNode;
    disabled?: boolean;
    content: RawDraftContentState | null;
    updateContent: (rawValue: RawDraftContentState | null) => void;
    hideToolbar?: boolean;
    width?: string;
    height?: string;
    headerLabel?: string;
    headerIcon?: React.FC;
}

export const PopoutWysiwygModal: React.FC<PopoutWysiwygModalProps> = ({ triggerElement, disabled = false, content, width = '900px', height = '200px', hideToolbar = false, updateContent, headerLabel, headerIcon = LongText }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [position, setPosition] = useState<Position | null>(null);

    const updateRawContent = useCallback((rawValue: RawDraftContentState) => {
        if (isEmpty(rawValue)) {
            if (!isNull(content)) {
                updateContent(null);
            }
        } else {
            updateContent(rawValue);
        }
    }, [updateContent, content]);

    const openModal = (x: number, y: number) => {
        setPosition({ x, y });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const positionModalHeight = useMemo(() => `${(parseInt(height) + MODAL_PADDING + (!isUndefined(headerLabel) ? MODAL_HEADER_AND_WYSIWYG_BORDER : 0))}px`, [height, headerLabel]);

    return (
        <>
            <div className={styles.popoutWrapper} onClick={e => openModal(e.clientX, e.clientY)}>
                {triggerElement}
            </div>
            <PositionModal
                isOpen={modalOpen}
                testId='popout-wysiwyg-modal'
                closeModal={closeModal}
                position={position}
                height={positionModalHeight}
                width={width}
            >
                {!isUndefined(headerLabel) && <ModalHeader label={headerLabel} icon={headerIcon} />}
                <WYSIWYG
                    content={content}
                    updateContent={updateRawContent}
                    toolbarHidden={hideToolbar}
                    disabled={disabled}
                    showBorder={false}
                    showWrapperBorder={!isUndefined(headerLabel)}
                    height={height}
                    maxHeight={height}
                    minHeight={height}
                />
            </PositionModal>
        </>
    );
};
